import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { Badge } from "../components/ui/badge"
import { cn } from "../lib/utils"
import { resolveIcon } from "./resolve-icon"
import { positionToken } from "../lib/design-tokens"
import { resolveColor } from "../lib/style-resolver"

export function BadgeRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : ""

  const variantMap: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    default: "default",
    secondary: "secondary",
    destructive: "destructive",
    outline: "outline",
  }

  const variant = element.variant ? variantMap[element.variant] ?? "default" : "default"

  // Resolve icon (same pattern as button-renderer)
  const rawIcon = element.icon ?? ""
  const iconName = rawIcon.includes("#{")
    ? String(resolveSymbol(rawIcon) ?? "")
    : rawIcon
  const IconComponent = iconName ? resolveIcon(iconName) : undefined

  // Resolve color via centralized resolver
  const { className: colorClass } = resolveColor(element.color as string | undefined)

  // Position classes (for absolute-positioned badges like cart badge)
  const posClasses: string[] = []
  if (element.position?.type === "absolute") {
    posClasses.push("absolute")
    if (element.position.top) {
      const v = positionToken(element.position.top)
      posClasses.push(v.startsWith("-") ? `-top-${v.slice(1)}` : `top-${v}`)
    }
    if (element.position.right) {
      const v = positionToken(element.position.right)
      posClasses.push(v.startsWith("-") ? `-right-${v.slice(1)}` : `right-${v}`)
    }
    if (element.position.left) {
      const v = positionToken(element.position.left)
      posClasses.push(v.startsWith("-") ? `-left-${v.slice(1)}` : `left-${v}`)
    }
    if (element.position.bottom) {
      const v = positionToken(element.position.bottom)
      posClasses.push(v.startsWith("-") ? `-bottom-${v.slice(1)}` : `bottom-${v}`)
    }
  }

  // Size classes for small badges (e.g., cart count)
  const sizeClasses = element.size === "xs"
    ? "flex h-5 w-5 items-center justify-center rounded-full p-0 text-[10px]"
    : ""

  const className = cn(...posClasses, sizeClasses, colorClass, element.className) || undefined

  const iconEl = IconComponent
    ? <IconComponent className={element.iconClassName ?? "h-3.5 w-3.5 mr-1.5"} />
    : null

  return (
    <Badge variant={variant} className={className}>
      {iconEl}
      {text}
    </Badge>
  )
}
